/**
 * Builds DataCard chart data from the same tag items used by StudySessionMasteryTagProgressCard.
 * Uses masteryTagsProgressData[].items so labels match (e.g. "Anatomy & Physiology", "Patient Care" in mocked flow).
 */

import type { MasteryTagProgressData } from "@helpers/content/types";
import type {
	BarChartData,
	CircularGaugeGridData,
	CircularProgressListData,
	RadarChartData,
} from "@memorang/ui";

type TagItemWithScore = MasteryTagProgressData["items"][number] & {
	score?: number;
	colorScale?: string[];
	label?: string;
};

const getTagLabel = (item: TagItemWithScore): string =>
	item.title ?? item.label ?? "Skill";

const randomHighScore = (min = 78, max = 96): number =>
	Math.floor(Math.random() * (max - min + 1)) + min;
const randomGoodMinutes = (min = 24, max = 38): number =>
	Math.floor(Math.random() * (max - min + 1)) + min;

const getTagItems = (
	masteryTagsProgressData: MasteryTagProgressData[],
): TagItemWithScore[] =>
	masteryTagsProgressData.flatMap((card) => card.items) as TagItemWithScore[];

const scoreToColor = (score: number): string => {
	if (score >= 75) {
		return "#66bb68";
	}
	if (score >= 50) {
		return "#9ccc65";
	}
	if (score >= 25) {
		return "#dda53a";
	}
	return "#d66565";
};

const clampPercent = (value: number): number =>
	Math.max(0, Math.min(100, Math.round(value)));

const normalizeScorePercent = (rawScore: number): number => {
	// Progress tags often carry a 0-30 scaled score. Normalize to 0-100.
	if (rawScore > 0 && rawScore <= 30) {
		return clampPercent((rawScore / 30) * 100);
	}
	return clampPercent(rawScore);
};

const getConfidenceLabel = (pct: number): string => {
	if (pct >= 88) {
		return "High confidence";
	}
	return "Good confidence";
};

const getLabelJitter = (label: string, magnitude = 4): number => {
	const hash = [...label].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
	const normalized = (hash % (magnitude * 2 + 1)) - magnitude;
	return normalized;
};

export const getRadarChartDataFromTags = (
	masteryTagsProgressData: MasteryTagProgressData[],
): RadarChartData => {
	const items = getTagItems(masteryTagsProgressData);
	if (items.length === 0) {
		return [
			{ skill: "Reading", value: 72, target: 80, peerAvg: 68 },
			{ skill: "Writing", value: 65, target: 75, peerAvg: 62 },
			{ skill: "Listening", value: 80, target: 85, peerAvg: 70 },
			{ skill: "Speaking", value: 58, target: 70, peerAvg: 55 },
		];
	}
	return items.map((item) => {
		const label = getTagLabel(item);
		const rawScore = item.score ?? 0;
		const baseScore =
			rawScore > 0 ? normalizeScorePercent(rawScore) : randomHighScore(76, 92);
		const jitter = getLabelJitter(label, 5);
		const value = clampPercent(baseScore + jitter);
		const target = clampPercent(
			value + 8 + Math.max(0, getLabelJitter(label, 3)),
		);
		const peerAvg = clampPercent(value - 6 + getLabelJitter(label, 2));
		return {
			skill: label,
			value,
			target,
			peerAvg,
		};
	});
};

export const getCircularProgressListDataFromTags = (
	masteryTagsProgressData: MasteryTagProgressData[],
): CircularProgressListData => {
	const items = getTagItems(masteryTagsProgressData);
	if (items.length === 0) {
		return [
			{
				label: "Cardiovascular",
				progress: 75,
				count: "24 items",
				color: "#66bb68",
			},
			{
				label: "Pharmacology",
				progress: 45,
				count: "18 items",
				color: "#dda53a",
			},
			{ label: "Anatomy", progress: 90, count: "32 items", color: "#66bb68" },
			{ label: "Pathology", progress: 30, count: "12 items", color: "#d66565" },
		];
	}
	return items.map((item) => {
		const score = normalizeScorePercent(item.score ?? 0);
		return {
			label: getTagLabel(item),
			progress: score,
			count: item.rightText ?? "0 items",
			color: item.colorScale?.[0] ?? scoreToColor(score),
		};
	});
};

export const getCircularGaugeGridDataFromTags = (
	masteryTagsProgressData: MasteryTagProgressData[],
): CircularGaugeGridData => {
	const items = getTagItems(masteryTagsProgressData);
	if (items.length === 0) {
		return [
			{
				label: "Reading",
				percentage: 92,
				fraction: "High confidence",
				color: "#66bb68",
			},
			{
				label: "Writing",
				percentage: 86,
				fraction: "Good confidence",
				color: "#66bb68",
			},
			{
				label: "Listening",
				percentage: 94,
				fraction: "High confidence",
				color: "#66bb68",
			},
			{
				label: "Speaking",
				percentage: 84,
				fraction: "Good confidence",
				color: "#66bb68",
			},
		];
	}
	return items.map((item) => {
		const rawScore = item.score ?? 0;
		const pct =
			rawScore > 0 ? normalizeScorePercent(rawScore) : randomHighScore();
		return {
			label: getTagLabel(item),
			percentage: pct,
			fraction: getConfidenceLabel(pct),
			color: item.colorScale?.[0] ?? (pct >= 88 ? "#66bb68" : "#9ccc65"),
		};
	});
};

export const getBarChartDataFromTags = (
	masteryTagsProgressData: MasteryTagProgressData[],
): BarChartData => {
	const items = getTagItems(masteryTagsProgressData);
	if (items.length === 0) {
		return [
			{
				category: "Reading",
				minutes: randomGoodMinutes(26, 36),
				status: "quick",
			},
			{
				category: "Writing",
				minutes: randomGoodMinutes(24, 34),
				status: "quick",
			},
			{
				category: "Listening",
				minutes: randomGoodMinutes(28, 38),
				status: "average",
			},
			{
				category: "Speaking",
				minutes: randomGoodMinutes(26, 36),
				status: "quick",
			},
		];
	}
	return items.map((item) => {
		const label = getTagLabel(item);
		const rawScore = item.score ?? 0;
		if (rawScore <= 0) {
			const minutes = Math.max(
				22,
				Math.min(60, randomGoodMinutes() + getLabelJitter(label, 7)),
			);
			return {
				category: label,
				minutes,
				status: minutes >= 45 ? "quick" : minutes >= 39 ? "average" : "slow",
			};
		}
		const score = normalizeScorePercent(rawScore);
		const baseMinutes = Math.round(32 + (score / 100) * 22);
		const minutes = Math.max(
			20,
			Math.min(60, baseMinutes + getLabelJitter(label, 8)),
		);
		let status: "quick" | "average" | "slow" = "average";
		if (minutes >= 45) {
			status = "quick";
		} else if (minutes < 39) {
			status = "slow";
		}
		return {
			category: label,
			minutes,
			status,
		};
	});
};
